import React, {useLayoutEffect, useState} from 'react';
import type {RefObject} from 'react';
import {ENTRY_CASH_GIFT_MASK_FADE_MS} from '../constants';

interface MaskGeometry {
  height: number;
  width: number;
  holeCx: number;
  holeCy: number;
  holeR: number;
}

export function CashGiftGuideMask({
  visible,
  fading,
  containerRef,
  holeRef,
}: {
  visible: boolean;
  fading: boolean;
  containerRef: RefObject<HTMLElement>;
  holeRef: RefObject<HTMLElement>;
}) {
  const [geometry, setGeometry] = useState<MaskGeometry>();

  useLayoutEffect(() => {
    if (!visible) {
      setGeometry(undefined);
      return;
    }

    const measure = () => {
      const container = containerRef.current;
      const hole = holeRef.current;
      if (!container || !hole) {
        return;
      }

      const containerRect = container.getBoundingClientRect();
      const holeRect = hole.getBoundingClientRect();
      if (containerRect.width <= 0 || containerRect.height <= 0) {
        return;
      }

      setGeometry({
        height: containerRect.height,
        width: containerRect.width,
        holeCx: holeRect.left - containerRect.left + holeRect.width / 2,
        holeCy: holeRect.top - containerRect.top + holeRect.height / 2,
        holeR: Math.min(holeRect.width, holeRect.height) * 0.38 * 1.4,
      });
    };

    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [containerRef, holeRef, visible]);

  if (!visible || !geometry) {
    return null;
  }

  return (
    <svg
      viewBox={`0 0 ${geometry.width} ${geometry.height}`}
      preserveAspectRatio="none"
      className={[
        'pointer-events-none absolute inset-0 z-[20] transition-opacity ease-out',
        fading ? 'opacity-0' : 'opacity-100',
      ].join(' ')}
      style={{transitionDuration: `${ENTRY_CASH_GIFT_MASK_FADE_MS}ms`}}
      aria-hidden="true">
      <defs>
        <mask
          id="refer-earn-cash-gift-guide-mask"
          maskUnits="userSpaceOnUse"
          x="0"
          y="0"
          width={geometry.width}
          height={geometry.height}>
          <rect width="100%" height="100%" fill="white" />
          <circle
            cx={geometry.holeCx}
            cy={geometry.holeCy}
            r={geometry.holeR}
            fill="black"
          />
        </mask>
      </defs>
      <rect
        width="100%"
        height="100%"
        fill="rgba(0,0,0,0.75)"
        mask="url(#refer-earn-cash-gift-guide-mask)"
      />
    </svg>
  );
}
